// src/pages/tour-packages/hooks/useUpdateTourPackage.ts

import { useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import {
  updateTour,
  type UpdateTourWithDetailsPayload,
  type TourHeader,
} from "@/api/features/tours";

export type UseUpdateTourPackageOptions = {
  /** Called after successful update (after cache invalidation and optional navigation). */
  onSuccess?: (tour: TourHeader, payload: UpdateTourWithDetailsPayload) => void;

  /** Called when update fails (network / validation / server error). */
  onError?: (error: Error, payload: UpdateTourWithDetailsPayload) => void;

  /**
   * If false, will NOT auto-navigate back to /tour/tour-packages on success.
   * Defaults to true.
   */
  navigateOnSuccess?: boolean;
};

/**
 * useUpdateTourPackage
 * --------------------
 * Wrapper around updateTour that:
 * - calls PUT /api/v1/tours/:id
 * - invalidates tours list + this tour detail
 * - optionally navigates back to /tour/tour-packages
 * - exposes hooks for onSuccess / onError
 */
export function useUpdateTourPackage(
  tourId: number,
  options?: UseUpdateTourPackageOptions
) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const navigateOnSuccess = options?.navigateOnSuccess ?? true;

  return useMutation<TourHeader, Error, UpdateTourWithDetailsPayload>({
    mutationFn: (payload) => updateTour(tourId, payload),

    onSuccess: async (tour, payload) => {
      // Invalidate list + detail cache
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["tours"] }),
        queryClient.invalidateQueries({ queryKey: ["tour-detail", tourId] }),
      ]);

      // Optional navigation
      if (navigateOnSuccess) {
        navigate("/tour/tour-packages");
      }

      // Optional caller callback
      options?.onSuccess?.(tour, payload);
    },

    onError: (error, payload) => {
      // Backend error.message already contains friendly text
      options?.onError?.(error, payload);
    },
  });
}
